import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { useUpdateExportSalesTrackerContract, SalesExportTrackerContract } from "@/lib/api/exportSalesTracking";
import { AlertCircle } from "lucide-react";

interface UpdateContractDialogProps {
  trackerId: string;
  contractId: string;
  initialData: {
    contract_number: string;
    has_been_signed: boolean;
  };
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function UpdateContractDialog({ 
  trackerId, 
  contractId, 
  initialData, 
  open, 
  onOpenChange, 
  onSuccess 
}: UpdateContractDialogProps) {
  const { toast } = useToast();
  const updateContract = useUpdateExportSalesTrackerContract();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contract_number: initialData.contract_number,
    has_been_signed: initialData.has_been_signed
  });
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      setFormData({
        contract_number: initialData.contract_number,
        has_been_signed: initialData.has_been_signed
      });
      setError(null);
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    if (isSubmitting) { return; }
    // Validate contract number
    if (!formData.contract_number.trim()) {
      setError("Contract number is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await updateContract(trackerId, contractId, {
        contract_number: formData.contract_number,
        has_been_signed: formData.has_been_signed
      });

      if (result.success) {
        toast({
          title: "Success",
          description: "Contract updated successfully"
        });
        onSuccess?.();
        onOpenChange(false);
      } else {
        setError(result.message || "Failed to update contract");
      }
    } catch (error) {
      setError("Network error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasChanges = formData.contract_number !== initialData.contract_number || 
                     formData.has_been_signed !== initialData.has_been_signed;

  return (
    <Dialog open={open} onOpenChange={(open) => {
      if (!open && !isSubmitting) {
        onOpenChange(false);
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Contract</DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div>
            <Label htmlFor="contract-number">Contract Number</Label>
            <Input
              id="contract-number"
              value={formData.contract_number}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, contract_number: e.target.value }));
                setError(null);
              }}
              placeholder="Enter contract number"
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="has-been-signed"
              checked={formData.has_been_signed}
              onCheckedChange={(checked) => 
                setFormData(prev => ({ ...prev, has_been_signed: !!checked }))
              }
              disabled={isSubmitting}
            />
            <Label htmlFor="has-been-signed" className="cursor-pointer">
              Has been signed
            </Label>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !hasChanges}
          >
            {isSubmitting ? "Updating..." : "Update Contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
